import { MASTER_DATA_ENTITIES, type MasterDataEntityKind } from '../lib/masterDataConfig.ts';
import type { ComparisonTopic, PageHelpEntry, PageHelpGuide } from './types.js';

/** Read-only teaching copy. Examples never create or change operational records. */
function entry(
  paths: readonly string[],
  id: string,
  title: string,
  purpose: string,
  example: string,
  steps: readonly string[],
  result: string,
  tip: string,
  comparison?: ComparisonTopic,
): PageHelpEntry {
  return {
    paths,
    guide: {
      id,
      title,
      purpose,
      example,
      steps,
      result,
      tips: [tip],
      ...(comparison ? { comparison } : {}),
    },
  };
}

interface MasterDataExplanation {
  readonly purpose: string;
  readonly example: string;
  readonly fields: string;
  readonly tip: string;
  readonly comparison?: ComparisonTopic;
}

const MASTER_EXPLANATIONS: Record<MasterDataEntityKind, MasterDataExplanation> = {
  party: {
    purpose:
      'افراد و سازمان‌هایی را تعریف یا تکمیل می‌کنی که با آن‌ها خرید، فروش یا حساب مالی داری؛ اطلاعات تماس و ثبتی می‌تواند بعداً تکمیل شود.',
    example:
      '«بیمارستان شهدا» نام طرف‌حساب است و نوع آن «سازمان» است؛ بیمارستان نوع جداگانه‌ای نیست. اگر هم مشتری و هم تأمین‌کننده است، همان پرونده می‌تواند هر دو نقش را داشته باشد.',
    fields:
      'نام و نوع «شخص» یا «سازمان» را مشخص کن و کد پیشنهادی را بررسی کن؛ نقش مشتری یا تأمین‌کننده را متناسب با کار انتخاب کن.',
    tip: 'پیش از ساخت، پروندهٔ تکراری را جست‌وجو کن؛ ورود دوبارهٔ فایل با همان کد، رکورد تازه نمی‌سازد و اطلاعات قبلی را تکمیل نمی‌کند. شناسه و تماس اختیاری‌اند؛ مقدار واردشده باید معتبر باشد. کد اقتصادی سازمان می‌تواند شناسهٔ ملی معتبر ۱۱ رقمی همان سازمان یا کد اقتصادی قدیمی ۱۲ رقمی باشد.',
    comparison: 'parties',
  },
  product: {
    purpose: 'کالاهایی را تعریف می‌کنی که بعداً در خرید، فروش و عملیات انبار انتخاب می‌شوند.',
    example:
      'برای «کاغذ A4» یک کالا با واحد پایهٔ «بسته» می‌سازی؛ رسیدن کالا به انبار در صفحهٔ رسید ثبت می‌شود.',
    fields: 'نام کالا و واحد سنجش پایه را مشخص کن؛ واحد باید با شیوهٔ شمارش کالای شما سازگار باشد.',
    tip: 'تعریف کالا به‌تنهایی موجودی ایجاد نمی‌کند؛ موجودی از عملیات انبار به‌وجود می‌آید.',
    comparison: 'products',
  },
  uom: {
    purpose: 'واحدهایی مثل عدد، بسته، متر یا کیلوگرم را برای شمارش مقدار کالا تعریف می‌کنی.',
    example:
      '«عدد» و «کارتن» دو واحد هستند؛ اینکه یک کارتن چند عدد است در تبدیل واحدها تعریف می‌شود.',
    fields: 'نام و کد واحد سنجش را بررسی کن؛ نام باید برای همکارانت روشن باشد.',
    tip: 'واحد سنجش با ضریب تبدیل فرق دارد؛ ساختن «کارتن» به‌تنهایی تعداد داخل کارتن را مشخص نمی‌کند.',
  },
  tax: {
    purpose: 'نرخ‌های مالیات مورد استفاده در اقلام و اسناد را تعریف می‌کنی.',
    example:
      'برای تمرینِ مفهوم، نرخ ۹ درصد به‌صورت کسر ۹ بر ۱۰۰ نوشته می‌شود؛ نرخ واقعی را مسئول مالی تعیین می‌کند.',
    fields: 'کد، نام و صورت و مخرج نرخ را مطابق نرخ مورد تأیید مسئول مالی وارد کن.',
    tip: 'صورت و مخرج، درصدِ نوشته‌شده با علامت ٪ نیستند؛ مثال آموزشی را به‌عنوان نرخ قانونی وارد نکن.',
  },
  warehouse: {
    purpose: 'انبارها یا محل‌های اصلی نگهداری کالا را به نرم‌افزار معرفی می‌کنی.',
    example:
      '«انبار مرکزی» و «انبار شعبه» دو انبار جدا هستند؛ جابه‌جایی کالا بین آن‌ها با انتقال ثبت می‌شود.',
    fields: 'کد، نام و وضعیت انبار را بررسی کن؛ نام را از نام مکان‌های داخلی آن جدا نگه دار.',
    tip: 'تعریف انبار به معنی ورود کالا به آن نیست؛ برای ورود واقعی باید رسید مناسب ثبت شود.',
    comparison: 'warehouse',
  },
  location: {
    purpose: 'مکان دقیق نگهداری کالا درون یک انبار را مشخص می‌کنی؛ مثل سالن یا قفسه.',
    example: '«قفسهٔ الف» داخل «انبار مرکزی» یک مکان انبار است و با یک انبار مستقل تفاوت دارد.',
    fields: 'ابتدا انبار مربوط، سپس کد و نام مکان را مشخص کن.',
    tip: 'نام یکسانِ دو قفسه در دو انبار، دلیل یکسان بودن آن‌ها نیست؛ انبارِ مربوط را هم کنترل کن.',
  },
  cost_center: {
    purpose:
      'بخش‌هایی را تعریف می‌کنی که هزینه‌ها به نام آن‌ها ثبت و در درخواست خرید انتخاب می‌شود.',
    example:
      '«دفتر مرکزی» و «فروش شمال» دو مرکز هزینه‌اند؛ هر درخواست خرید به یکی از آن‌ها نسبت داده می‌شود.',
    fields: 'نام مرکز هزینه را روشن بنویس؛ کد پیشنهادی سیستم را نگه دار یا تغییر بده.',
    tip: 'مرکز هزینه‌ای که قبلاً در درخواست خرید استفاده شده حذف نمی‌شود؛ آرشیو می‌شود تا سابقه بماند.',
  },
  price_list: {
    purpose: 'قیمت فروش هر کالا را به تفکیک واحد، برای یک بازهٔ زمانی مشخص تعریف می‌کنی.',
    example:
      'در لیست «فروش عمده»، یک کارتن خودکار ۱۰۰٬۰۰۰ ریال و هر عدد ۹٬۰۰۰ ریال است؛ هنگام پیش‌فاکتور همان لیست را انتخاب می‌کنی.',
    fields: 'نام و بازهٔ اعتبار را بنویس، سپس برای هر کالا واحد و قیمت را وارد کن.',
    tip: 'کالایی که در لیست قیمت نیست با قیمت خودِ کالا در پیش‌فاکتور می‌آید؛ قیمت هر ردیف همیشه قابل ویرایش است.',
  },
};

const MASTER_HELP_ENTRIES: readonly PageHelpEntry[] = MASTER_DATA_ENTITIES.flatMap((entity) => {
  const copy = MASTER_EXPLANATIONS[entity.kind];
  const path = `/master-data/${entity.pathSegment}`;
  const common = {
    purpose: copy.purpose,
    example: copy.example,
    tips: [copy.tip],
    ...(copy.comparison ? { comparison: copy.comparison } : {}),
  } satisfies Pick<PageHelpGuide, 'purpose' | 'example' | 'tips' | 'comparison'>;
  return [
    {
      paths: [path],
      guide: {
        ...common,
        id: `master-${entity.kind}-list`,
        title: entity.labelPlural,
        steps:
          entity.kind === 'party'
            ? [
                'نام یا کد طرف‌حساب را جست‌وجو کن؛ برای تکمیل اطلاعات، همان ردیف موجود را باز کن.',
                'برای مورد تازه، «ایجاد طرف‌حساب» را انتخاب کن؛ در فرم دستی کد پیشنهاد می‌شود و نام و نوع را مشخص می‌کنی.',
                'اگر از فایل وارد می‌کنی، کد، نام و نوع را وارد کن؛ در ورود تک‌جدولی، نوع person برای شخص یا organization برای سازمان است.',
                'مشخصات تماس و ثبتی می‌تواند خالی بماند و بعداً در ویرایش تکمیل شود؛ وضعیت فهرست و نتیجهٔ ورود فایل را هم بررسی کن.',
              ]
            : [
                `نام یا کد ${entity.label} را جست‌وجو کن و نتیجه را بررسی کن.`,
                `برای مورد تازه، «ایجاد ${entity.label}» را انتخاب کن؛ برای مورد موجود، همان ردیف را باز کن.`,
                'اگر موردی آرشیو شده، وضعیت فهرست را هم بررسی کن.',
              ],
        result:
          entity.kind === 'party'
            ? 'پروندهٔ درست را برای استفاده یا تکمیل پیدا می‌کنی؛ ثبت مورد تازه فقط پس از ذخیره یا پایان موفق ورود فایل انجام می‌شود.'
            : `رکورد درست را پیدا می‌کنی یا به فرم تعریف ${entity.label} می‌روی.`,
      },
    },
    {
      paths: [`${path}/new`],
      guide: {
        ...common,
        id: `master-${entity.kind}-new`,
        title: `ایجاد ${entity.label}`,
        steps:
          entity.kind === 'party'
            ? [
                copy.fields,
                'برای ثبت اولیه، تماس و شناسه‌های اختیاری را می‌توانی خالی بگذاری؛ اگر وارد می‌کنی، با نوع شخص یا سازمان تطبیق بده.',
                'نام، نوع و کد را دوباره بخوان؛ فرم را ذخیره کن و پیام نتیجه را بررسی کن.',
              ]
            : [
                copy.fields,
                'فیلدهای الزامی را کامل کن و اطلاعات را دوباره بخوان.',
                'فرم را ذخیره کن و پیام نتیجه را بررسی کن.',
              ],
        result:
          entity.kind === 'party'
            ? 'پس از ذخیرهٔ موفق، طرف‌حساب برای بخش‌های مرتبط آماده است؛ اطلاعات اختیاری را بعداً از ویرایش کامل می‌کنی. این کار به‌تنهایی تراکنش مالی یا انباری ثبت نمی‌کند.'
            : `${entity.label} تازه برای استفاده در بخش‌های مرتبط تعریف می‌شود؛ این کار به‌تنهایی تراکنش مالی یا انباری ثبت نمی‌کند.`,
      },
    },
    {
      paths: [`${path}/:id`],
      guide: {
        ...common,
        id: `master-${entity.kind}-edit`,
        title: `بررسی و ویرایش ${entity.label}`,
        steps:
          entity.kind === 'party'
            ? [
                'نام و کد را کنترل کن تا پروندهٔ درست را باز کرده باشی؛ برای تکمیل رکورد موجود، آن را با همان کد در فهرست پیدا کن.',
                'نوع شخص یا سازمان و نقش را بررسی کن؛ تماس و مشخصات ثبتیِ موجود را در همین فرم تکمیل کن.',
                'شناسه‌ها را متناسب با نوع طرف‌حساب بررسی کن؛ اطلاعات اختیاری نامعلوم را خالی بگذار و مقدار ساختگی وارد نکن.',
                'در صورت داشتن دسترسی، تغییرات را ذخیره کن و پیام نتیجه را بخوان؛ ورود دوبارهٔ همان کد جای ویرایش نیست.',
              ]
            : [
                'نام و کد بالای فرم را بررسی کن تا مطمئن شوی رکورد درست را باز کرده‌ای.',
                copy.fields,
                'در صورت داشتن دسترسی، تغییرات موردنیاز را ذخیره کن و پیام نتیجه را بخوان.',
              ],
        result:
          entity.kind === 'party'
            ? 'پس از ذخیرهٔ موفق، مشخصات همین طرف‌حساب تکمیل می‌شود؛ پروندهٔ تازه‌ای ساخته نمی‌شود.'
            : `مشخصات همین ${entity.label} بررسی یا به‌روزرسانی می‌شود؛ رکورد تازه‌ای ساخته نمی‌شود.`,
      },
    },
  ];
});

export const OPERATIONS_HELP_ENTRIES: readonly PageHelpEntry[] = [
  entry(
    ['/master-data'],
    'master-data-home',
    'اطلاعات پایه',
    'اطلاعاتی را تعریف می‌کنی که بقیهٔ بخش‌ها بارها از آن استفاده می‌کنند؛ مثل طرف‌حساب، کالا، واحد و انبار.',
    'پیش از ثبت فروش کاغذ، باید مشتری، خودِ کالا و واحد شمارش آن تعریف شده باشند.',
    [
      'کارت مربوط به اطلاعات موردنیازت را انتخاب کن.',
      'اول فهرست را جست‌وجو کن؛ اگر مورد موجود نیست، فرم ایجاد آن را باز کن.',
      'بعد از ذخیره، به فرایند خرید، فروش یا انبار برگرد.',
    ],
    'فهرست‌های مشترک شرکت آماده می‌شوند تا در عملیات بعدی از یک تعریف یکسان استفاده شود.',
    'اطلاعات پایه با سند عملیاتی فرق دارد؛ ساختن کالا، مشتری یا انبار به‌تنهایی خرید یا فروش نیست.',
  ),
  entry(
    ['/master-data/brand-assets'],
    'brand-assets',
    'مهر، امضا و لوگوی شرکت',
    'تصویرهای سازمان را برای نمایش در خروجی پیش‌فاکتور تنظیم می‌کنی.',
    'برای اینکه پیش‌فاکتور با مهر شرکت چاپ شود، تصویر مهرِ تأییدشده را در جای مخصوص آن قرار می‌دهی.',
    [
      'کارت لوگو، مهر یا امضا را پیدا کن.',
      'در صورت داشتن مجوز، تصویر درست را بارگذاری کن و پیش‌نمایش را ببین.',
      'خروجی پیش‌فاکتور را بررسی کن تا اندازه و خوانایی تصویر مناسب باشد.',
    ],
    'تصویر تنظیم‌شده در جای مربوط به خودش در پیش‌فاکتور استفاده می‌شود.',
    'تصویر مهر با امضای دیجیتال یا تأیید مالی یکسان نیست؛ فقط تصویر سازمانی تأییدشده را بارگذاری کن.',
  ),
  ...MASTER_HELP_ENTRIES,
  entry(
    ['/crm'],
    'crm-home',
    'فروش و ارتباط با مشتری',
    'تصویر کلی فروش را می‌بینی و از همین بخش به سرنخ‌ها، مشتریان، پیش‌فاکتورها و سفارش‌ها می‌روی.',
    'فردی دربارهٔ محصول سؤال می‌کند؛ ابتدا سرنخ او را پیگیری می‌کنی و پس از توافق سراغ پیش‌فاکتور می‌روی.',
    [
      'خلاصه‌های فروش و موارد نیازمند پیگیری را مرور کن.',
      'از تب مناسب، سرنخ، مشتری یا سند فروش را باز کن.',
      'برای ادامهٔ کار، جزئیات همان مورد را ببین.',
    ],
    'می‌فهمی کدام مرحلهٔ فروش نیاز به اقدام دارد و به صفحهٔ مربوط می‌روی.',
    'سرنخ، پیش‌فاکتور، سفارش و فاکتور مراحل متفاوت‌اند؛ مبلغ آن‌ها را به‌عنوان یک فروش واحد با هم جمع نکن.',
  ),
  entry(
    ['/crm/leads', '/crm/opportunities'],
    'crm-leads',
    'سرنخ‌ها و مسیر فروش',
    'افراد یا شرکت‌های علاقه‌مند به خرید را ثبت و مرحلهٔ مذاکره با آن‌ها را دنبال می‌کنی.',
    'یک شرکت درخواست دمو داده است؛ آن را به‌عنوان سرنخ ثبت می‌کنی و اقدام بعدی را مشخص می‌کنی.',
    [
      'پیش از ساخت، نام یا شمارهٔ مخاطب را جست‌وجو کن.',
      'سرنخ را باز کن و مسئول، مرحله و اقدام بعدی را بررسی کن.',
      'نتیجهٔ پیگیری را ثبت کن و مرحله را مطابق اتفاق واقعی تغییر بده.',
      'در فرم سرنخ، فیلد «برچسب‌ها» را برای دسته‌بندی پر کن: برچسب موجود را انتخاب کن یا برچسب تازه با رنگ بساز؛ برچسب‌ها روی فهرست، تابلو و جزئیات سرنخ دیده می‌شوند.',
    ],
    'سوابق و وضعیت مذاکره روشن می‌ماند و پیگیری بعدی فراموش نمی‌شود.',
    'جابه‌جایی سرنخ در مسیر فروش به‌تنهایی فاکتور یا دریافت پول ثبت نمی‌کند. برچسب فقط دسته‌بندی است و مرحله یا دسترسی را عوض نمی‌کند.',
  ),
  entry(
    ['/crm/activities'],
    'crm-activities',
    'فعالیت‌ها و پیگیری‌ها',
    'تماس، جلسه و اقدام‌های پیگیری فروش را با زمان و موضوع مشخص نگه می‌داری.',
    'قرار است سه‌شنبه دوباره با مشتری دربارهٔ پیش‌فاکتور صحبت کنی؛ این پیگیری را ثبت می‌کنی.',
    [
      'فهرست فعالیت‌ها و موارد عقب‌افتاده را بررسی کن.',
      'فعالیت مربوط به مشتری یا سرنخ درست را باز کن.',
      'پس از انجام کار، نتیجه و وضعیت آن را مطابق واقعیت ثبت کن.',
    ],
    'همکاران می‌دانند چه پیگیری‌ای انجام شده و چه کاری هنوز باقی مانده است.',
    'ثبت برنامهٔ یک تماس به معنی برقرار شدن تماس یا ثبت فایل مکالمه نیست.',
  ),
  entry(
    ['/crm/quotes'],
    'crm-quotes',
    'پیش‌فاکتورها',
    'پیشنهاد قیمت برای مشتری آماده می‌کنی و پیش‌فاکتورهای قبلی را پیدا می‌کنی.',
    'مشتری قیمت ده بسته کاغذ می‌خواهد؛ کالا، تعداد، قیمت و مهلت اعتبار را در پیش‌فاکتور مشخص می‌کنی.',
    [
      'فهرست را با شماره یا نام طرف‌حساب جست‌وجو کن.',
      'برای مورد تازه، طرف‌حساب و اقلام را در فرم صفحه کامل کن. اگر از میانبر «پیش‌فاکتور جدید» در پروندهٔ مشتری آمده باشی، طرف‌حساب از قبل انتخاب شده است؛ آن را کنترل کن.',
      'پس از ثبت، پیش‌فاکتور را باز کن و اطلاعات و مهلت اعتبار را بررسی کن.',
    ],
    'یک پیشنهاد قابل پیگیری برای مشتری داری؛ ادامهٔ فرایند از جزئیات پیش‌فاکتور انجام می‌شود.',
    'پیش‌فاکتور به‌تنهایی به معنی تحویل کالا، فاکتور قطعی یا دریافت وجه نیست.',
    'quote',
  ),
  entry(
    ['/crm/quotes/:ref'],
    'crm-quote-detail',
    'جزئیات پیش‌فاکتور',
    'اقلام و وضعیت یک پیش‌فاکتور را بررسی می‌کنی و مسیر آن تا سفارش و فاکتور را دنبال می‌کنی.',
    'مشتری پیشنهاد قیمت را پذیرفته است؛ پیش از تبدیل، مقدار و قیمت اقلام سفارش را بررسی می‌کنی.',
    [
      'طرف‌حساب، اقلام، مبلغ و تاریخ اعتبار را کنترل کن.',
      'اگر به خروجی نیاز داری، «خروجی HTML / چاپ» را باز کن؛ لوگو و مهر از «اطلاعات پایه» می‌آیند و اگر ثبت نشده باشند در برگه دیده نمی‌شوند.',
      'برای ادامهٔ خرید مشتری، در صورت داشتن مجوز «تبدیل به سفارش / فاکتور» را باز کن و توضیح فرایند را بخوان.',
    ],
    'می‌توانی پیشنهاد را چاپ کنی یا سفارش مرتبط بسازی؛ صدور فاکتور از مرحلهٔ مناسبِ سفارش انجام می‌شود.',
    'ویرایش پیش‌فاکتور ارسال‌شده آن را به پیش‌نویس برمی‌گرداند؛ پس از تغییر، ارسال مجدد را فراموش نکن.',
    'quote',
  ),
  entry(
    ['/crm/orders'],
    'crm-orders',
    'سفارش‌های فروش',
    'تعهد فروش به مشتری را ثبت می‌کنی و می‌بینی هر سفارش چقدر تأمین یا تحویل شده است.',
    'مشتری بیست کالا سفارش داده و ده عدد آن تحویل شده است؛ مقدار باقی‌مانده را در سفارش دنبال می‌کنی.',
    [
      'شمارهٔ سفارش یا طرف‌حساب را جست‌وجو کن.',
      'برای سفارش تازه، طرف‌حساب و پیش‌فاکتور مرتبط یا اقلام مستقل را مشخص کن. اگر از میانبر «سفارش جدید» در پروندهٔ مشتری آمده باشی، طرف‌حساب از قبل انتخاب شده است؛ آن را کنترل کن.',
      'جزئیات سفارش را باز کن تا وضعیت رزرو، تحویل و فاکتور را ببینی.',
    ],
    'سفارش و مقدار تحویل‌شدهٔ آن قابل پیگیری می‌شود.',
    'ثبت سفارش به‌تنهایی به معنی خروج کالا از انبار یا وصول وجه نیست.',
  ),
  entry(
    ['/crm/orders/:ref'],
    'crm-order-detail',
    'جزئیات و تحویل سفارش فروش',
    'اقلام یک سفارش، تأمین کالا، تحویل و فاکتورهای مربوط به آن را کنار هم می‌بینی.',
    'از ده قلم سفارش، فقط شش قلم تحویل شده است؛ فاکتور را بر اساس مقدار قابل صدور بررسی می‌کنی.',
    [
      'مشتری، اقلام و مقدار سفارش را کنترل کن.',
      'وضعیت رزرو و تحویل هر ردیف را بررسی کن و فقط اقدام مجازِ متناسب با مرحله را انجام بده.',
      'اگر «صدور فاکتور» در دسترس است، اقلام قابل صدور و توضیح اثر مالی را پیش از تأیید بخوان.',
    ],
    'می‌دانی چه مقدار باقی مانده و چه اسنادی برای همین سفارش ساخته شده‌اند.',
    'برای کالای تحویل‌نشده، تحویل صوری ثبت نکن؛ مقدار سفارش با مقدار تحویل و مقدار فاکتور یکسان فرض نمی‌شود.',
  ),
  entry(
    ['/crm/invoices'],
    'crm-invoices',
    'فاکتورهای فروش',
    'فاکتورهای فروش را با مشتری، تاریخ و وضعیت پیدا می‌کنی.',
    'می‌خواهی فاکتور هفتهٔ قبلِ یک مشتری را ببینی؛ نام مشتری و بازهٔ تاریخ را مشخص می‌کنی.',
    [
      'شماره، نام مشتری یا بازهٔ تاریخ را در فیلترها وارد کن.',
      '«اعمال فیلتر» را انتخاب کن و وضعیت فاکتور را ببین.',
      'ردیف فاکتور را باز کن تا اقلام و مدارک مرتبط را بررسی کنی.',
    ],
    'فاکتور موردنظر را با وضعیت واقعی آن پیدا می‌کنی.',
    'فاکتور پیش‌نویس با فاکتور ثبت قطعی فرق دارد؛ فقط وجود یک ردیف در این فهرست به معنی فروش قطعی نیست.',
    'invoice',
  ),
  entry(
    ['/crm/invoices/:invoiceId'],
    'crm-invoice-detail',
    'مشاهدهٔ فاکتور فروش',
    'جزئیات یک فاکتور، اقلام و ارتباط آن با سفارش و مدارک مالی را بررسی می‌کنی.',
    'مشتری دربارهٔ مبلغ فاکتور سؤال دارد؛ مقدار، قیمت و جمع اقلام همان فاکتور را کنترل می‌کنی.',
    [
      'نام مشتری، شماره، تاریخ و وضعیت فاکتور را بررسی کن.',
      'اقلام، مقدار و مبلغ‌ها را با توافق و سفارش تطبیق بده.',
      'برای بررسی مرحلهٔ قبل یا اثر مالی، پیوندهای مرتبطِ موجود در صفحه را باز کن.',
      'برای دادن نسخه به مشتری، «خروجی HTML / چاپ» را بزن؛ برگهٔ فاکتور با لوگو و اطلاعات فروشنده باز می‌شود و می‌توانی چاپ یا ذخیره کنی.',
    ],
    'مبنای مبلغ فاکتور و ارتباط آن با فرایند فروش را می‌فهمی و در صورت نیاز نسخهٔ چاپی داری.',
    'این صفحه برای مشاهدهٔ فاکتور است؛ برای اصلاح سند قطعی، فرایند مجاز اصلاح یا برگشت را با مسئول مالی بررسی کن. نسخهٔ چاپیِ فاکتورِ هنوز قطعی‌نشده یا برگشت‌خورده با علامت «پیش‌نویس» یا «برگشت‌شده» چاپ می‌شود و معتبر نیست؛ لوگو و مهر از «اطلاعات پایه» می‌آیند.',
    'invoice',
  ),
  entry(
    ['/crm/returns'],
    'crm-returns',
    'برگشت از فروش',
    'برگشت‌های مرتبط با فروش را بررسی و در صورت داشتن مجوز، فرایند برگشت را پیگیری می‌کنی.',
    'مشتری بخشی از کالای فروخته‌شده را برگردانده است؛ برگشت باید به فروش و مقدار درست مربوط باشد.',
    [
      'فروش یا برگشت موردنظر را در صفحه پیدا کن.',
      'مرجع فروش، طرف‌حساب و مقدار مربوط را بررسی کن.',
      'پیش از ثبت هر اقدام، جزئیات و پیام تأیید را با مسئول فروش یا مالی کنترل کن.',
    ],
    'برگشت با سابقهٔ فروش قابل پیگیری می‌شود.',
    'برگشت فروش، بازپرداخت پول و ورود فیزیکی کالا به انبار را یک اتفاق یکسان فرض نکن؛ مدارک هر مرحله را بررسی کن.',
  ),
  entry(
    ['/crm/customers'],
    'crm-customers',
    'مشتریان',
    'مشتری موجود را پیدا می‌کنی یا با نام و نوع، مشتری تازه می‌سازی؛ اطلاعات اختیاری را می‌توانی بعداً کامل کنی.',
    'برای «بیمارستان شهدا»، همین عبارت را در نام بنویس و نوع «شخص حقوقی» را انتخاب کن؛ بیمارستان نوع جداگانه‌ای نیست.',
    [
      'نام، کد یا شمارهٔ مشتری را جست‌وجو کن تا پروندهٔ تکراری نسازی.',
      'برای مشتری تازه، فرم ایجاد را باز کن؛ نام و نوع حقیقی یا حقوقی را مشخص کن و کد پیشنهادی را بررسی کن.',
      'تماس و مشخصات ثبتی اختیاری را می‌توانی خالی بگذاری؛ تغییر نوع، اطلاعات پیش‌نویس را نگه می‌دارد، پس شناسه‌های متناسب با نوع را دوباره بررسی کن.',
      'برای دسته‌بندی، در فیلد «برچسب‌ها» برچسب موجود را انتخاب کن یا برچسب تازه با رنگ بساز؛ بعداً با فیلتر «برچسب» فهرست را محدود کن.',
      'فرم را ثبت کن و نتیجه را بخوان؛ بعد از ثبت موفق، پرونده را برای ادامهٔ کار یا تکمیل اطلاعات باز کن.',
    ],
    'مشتری موجود را پیدا می‌کنی یا پس از ثبت موفق، پروندهٔ تازه‌ای داری؛ تغییر نوع یا پرکردن فرم به‌تنهایی ثبت نیست.',
    'یک طرف‌حساب می‌تواند مشتری و تأمین‌کننده باشد؛ ورود دوبارهٔ فایل با همان کد، اطلاعات موجود را تکمیل نمی‌کند و باید ویرایش کنی. شناسه‌ها اختیاری‌اند؛ کد اقتصادی حقوقی، شناسهٔ ملی معتبر ۱۱ رقمی همان سازمان یا کد اقتصادی قدیمی ۱۲ رقمی است. برچسب فقط دسته‌بندی است و روی دسترسی یا مالی مشتری اثری ندارد.',
    'parties',
  ),
  entry(
    ['/crm/customers/:ref'],
    'crm-customer-detail',
    'پروندهٔ مشتری',
    'مشخصات، معاملات، پیگیری‌ها و ارتباطات یک مشتری را در یک پرونده می‌بینی و در تب «نمای کلی» تحلیل ساختار یار دربارهٔ او را می‌خوانی.',
    'مشتری دربارهٔ پیشنهاد قبلی تماس می‌گیرد؛ پیش‌فاکتور و سابقهٔ گفت‌وگو را مرور می‌کنی و برای جمع‌بندی سریع، در «نمای کلی» دکمهٔ «تحلیل با هوش مصنوعی» را می‌زنی.',
    [
      'نام و کد مشتری را کنترل کن.',
      'در «نمای کلی»، با «تحلیل با هوش مصنوعی» همهٔ سوابق مجاز این مشتری را یک‌جا تحلیل کن: معاملات، پیش‌فاکتور و سفارش، دریافتنی‌ها، فعالیت‌ها، یادداشت‌ها، تماس‌ها و پیامک‌ها.',
      'در کارت تحلیل، سلامت رابطه، ریسک ریزش، نقاط قوت، مسائل باز و پیشنهاد اقدام بعدی را بخوان و ببین تحلیل بر پایهٔ چه سوابقی ساخته شده است.',
      'برچسب‌های رنگی زیر نام مشتری دسته‌بندی او را نشان می‌دهند و رنگ پرونده از اولین برچسب رنگی گرفته می‌شود؛ با «ویرایش مشتری» می‌توانی برچسب‌ها را عوض کنی.',
      'از میانبرهای زیر عنوان (پیش‌فاکتور، سفارش، معامله، فعالیت، یادداشت، پیامک، تماس) برای شروع کار روی همین مشتری استفاده کن؛ فقط میانبرهایی که دسترسی‌اش را داری دیده می‌شوند.',
      'تب متناسب با کار خودت، مثل مشخصات، فروش یا ارتباطات را باز کن.',
      'برای سند یا تماس مشخص، جزئیات همان مورد را ببین؛ اطلاعات هر تب تابع دسترسی تو است.',
    ],
    'پیش از اقدام بعدی، تصویر روشن‌تری از رابطهٔ شرکت با همین مشتری داری.',
    'تحلیل ساختار یار هر بار با کلیک تو اجرا می‌شود و از اعتبار هوش مصنوعی سازمان مصرف می‌کند؛ فقط سوابقی را می‌خواند که خودت اجازهٔ دیدنشان را داری و بخش‌های دیده‌نشده را در کارت می‌نویسد. نتیجه‌اش پیشنهاد است؛ پیش از تصمیم مهم با خود سوابق تطبیق بده. در «ویرایش مشتری» فیلدهایی که اجازهٔ تغییرشان را نداری قفل هستند و فقط موارد تغییرکرده ذخیره می‌شود. دریافتنی افتتاحیهٔ پیش‌نویس با ماندهٔ قطعی همهٔ حساب‌های مشتری یکی نیست؛ عنوان و محدودهٔ هر عدد را بخوان.',
    'parties',
  ),
  entry(
    ['/crm/call-base'],
    'crm-call-base',
    'پایگاه تماس',
    'تماس‌های ثبت‌شدهٔ ورودی و خروجی، فایل مکالمه و وضعیت تحلیل آن‌ها را بررسی می‌کنی.',
    'یک تماس بی‌پاسخ را پیدا می‌کنی تا مشتری را شناسایی و پیگیری کنی؛ برای تماس ضبط‌شده می‌توانی متن و تحلیل موجود را ببینی.',
    [
      'نام، شماره یا وضعیت تماس را جست‌وجو یا فیلتر کن.',
      'ردیف تماس را باز کن و تب اطلاعات، فایل مکالمه، متن یا تحلیل را ببین.',
      'برای بارگذاری یا تحلیل، فقط از اقدام‌های در دسترس استفاده کن و پیام مصرف اعتبار یا نیاز به تأیید را بخوان.',
    ],
    'وضعیت تماس، هویت مخاطب و اطلاعات موجود برای پیگیری روشن می‌شود.',
    'هر تماس الزاماً فایل ضبط یا تحلیل ندارد؛ نتیجهٔ تحلیل را با مکالمه و پروندهٔ مشتری بررسی کن.',
  ),
  entry(
    ['/crm/sms-panel'],
    'crm-sms',
    'پنل پیامک',
    'قالب متن پیامک، روال‌های خودکار و تاریخچهٔ پیامک مشتری را مدیریت یا مشاهده می‌کنی.',
    'می‌خواهی متن یک پیام خودکار را بررسی کنی و بعد ببینی چه پیام‌هایی برای یک مشتری ثبت شده است.',
    [
      'تب «قالب‌ها»، «روال‌های خودکار» یا «تاریخچه مشتری» را بر اساس کار خودت انتخاب کن.',
      'متن و تنظیمات همان مورد را بررسی کن.',
      'پیش از فعال‌کردن روال، زمان و مخاطب ارسال و متغیرهای متن را کنترل کن.',
    ],
    'متن و شرایط ارسال پیامک روشن می‌شود و سابقهٔ مشتری قابل بررسی است.',
    'ساخت قالب با ارسال پیامک فرق دارد؛ فعال‌کردن روال می‌تواند بر ارسال‌های بعدی اثر بگذارد. در «تاریخچه مشتری»، زیر فهرست پیام‌ها همان تحلیل ساختار یار از مشتری هست؛ هر بار اجرایش از اعتبار هوش مصنوعی سازمان مصرف می‌کند.',
  ),
  entry(
    ['/crm/my-sales'],
    'crm-my-sales',
    'فروش و پورسانت من',
    'فروش‌های قطعی مربوط به خودت، پورسانت و بخش آزادشدهٔ آن را می‌بینی.',
    'برای یک فروش پورسانت ثبت شده، اما مشتری هنوز همهٔ مبلغ را نداده است؛ مبلغ آزادشده را جدا بررسی می‌کنی.',
    [
      'خلاصهٔ فروش، پورسانت و مبلغ آزادشده را مرور کن.',
      'فاکتور را با شماره، سفارش یا مشتری پیدا کن.',
      'وضعیت فاکتور و وصول را کنار مبلغ پورسانت همان ردیف بخوان.',
    ],
    'می‌فهمی چه مقدار پورسانت ثبت و چه مقدار بر اساس وصول آزاد شده است.',
    'پورسانت ثبت‌شده، پورسانت آزادشده و پرداخت واقعی پورسانت سه مفهوم جدا هستند.',
  ),
  entry(
    ['/crm/team', '/crm/commission'],
    'crm-team',
    'عملکرد تیم فروش',
    'عملکرد فروشندگان را در یک بازه بررسی می‌کنی و در صورت داشتن مجوز، تنظیمات پورسانت آن‌ها را می‌بینی.',
    'می‌خواهی فروش و وصول این ماهِ دو فروشنده را مقایسه کنی؛ هر دو را در همان بازه بررسی می‌کنی.',
    [
      'بازهٔ گزارش و شاخص‌های نمایش‌داده‌شده را بررسی کن.',
      'فروش، وصول و پورسانت هر فرد را با هم بخوان.',
      'برای جزئیات، پروفایل همان فروشنده را باز کن.',
    ],
    'می‌توانی عملکرد تیم و موارد نیازمند پیگیری را با دادهٔ همان بازه بررسی کنی.',
    'مقایسهٔ فروش بدون توجه به بازه، وصول و وضعیت فاکتورها می‌تواند برداشت اشتباه ایجاد کند.',
  ),
  entry(
    ['/crm/team/:salespersonId'],
    'crm-salesperson',
    'پروفایل فروشنده',
    'فروش، تماس‌ها، فعالیت‌ها و پورسانت یک فروشنده را در یک صفحه بررسی می‌کنی.',
    'فروش یک نفر کم شده است؛ تماس‌ها و پیگیری‌های همان دوره را کنار نتیجهٔ فروش مرور می‌کنی.',
    [
      'نام فروشنده و بازهٔ زمانی را کنترل کن.',
      'تب «مرور کلی»، «تماس‌ها»، «فروش و پورسانت» یا «فعالیت‌ها» را باز کن.',
      'رتبه و مقایسه با تیم را فقط با توجه به همان بازه و دادهٔ موجود تفسیر کن.',
    ],
    'جزئیات عملکرد همین فرد و ارتباط شاخص‌های او را می‌بینی.',
    'خلاصهٔ تحلیل تماس فقط برای تماس‌های تحلیل‌شده است و نمایندهٔ همهٔ تماس‌ها نیست.',
  ),
  entry(
    ['/inventory'],
    'inventory-home',
    'مرور انبار',
    'خلاصهٔ وضعیت انبار را می‌بینی و به موجودی، ورود، خروج، انتقال و شمارش کالا دسترسی پیدا می‌کنی.',
    'برای پاسخ به فروش، موجودی قابل استفاده را بررسی می‌کنی و در صورت نیاز گردش همان کالا را باز می‌کنی.',
    [
      'خلاصه‌ها و هشدارهای انبار را مرور کن.',
      'تب مربوط به کار خودت را انتخاب کن.',
      'کالا، انبار و محل را در صفحهٔ مقصد مشخص کن.',
    ],
    'مسیر درست مشاهده یا ثبت عملیات انبار را پیدا می‌کنی.',
    'موجودی کل با موجودی قابل استفاده فرق دارد؛ کالا ممکن است رزرو یا مسدود شده باشد.',
    'inventory',
  ),
  entry(
    ['/inventory/stock'],
    'inventory-stock',
    'موجودی کالا',
    'مقدار ثبت‌شدهٔ کالا در انبار و مقدار قابل استفادهٔ آن را می‌بینی.',
    'ده عدد کالا موجود است و چهار عدد آن رزرو شده؛ برای قول تحویل به مشتری، مقدار قابل استفاده را بررسی می‌کنی.',
    [
      'کالا یا انبار موردنظر را در فهرست پیدا کن.',
      'موجودی کل، رزرو و قابل استفاده را کنار هم بخوان.',
      'اگر عددی نیاز به بررسی دارد، گردش کالا را در کاردکس دنبال کن.',
    ],
    'می‌دانی چه مقدار کالا ثبت شده و چه مقدار برای کار بعدی قابل استفاده است.',
    'جمع تعداد کالاهای با واحد متفاوت، مثل کیلوگرم و عدد، معنای یک موجودی واحد ندارد.',
    'inventory',
  ),
  entry(
    ['/inventory/cardex'],
    'inventory-cardex',
    'کاردکس کالا',
    'داستان ورود و خروج یک کالا را به ترتیب گردش‌ها می‌بینی؛ اینکه مقدار و ارزش آن چطور تغییر کرده است.',
    'ماندهٔ یک کالا با انتظار تو فرق دارد؛ رسیدها، حواله‌ها و تعدیل‌های مربوط را در کاردکس بررسی می‌کنی.',
    [
      'کالا، انبار و محدودهٔ گزارش را مشخص کن.',
      'گردش‌ها را به‌ترتیب بخوان و ورود، خروج و مانده را مقایسه کن.',
      'برای بررسی علت یک گردش، سند مربوط به همان ردیف را باز کن.',
    ],
    'علت تغییر موجودی را در سوابق عملیاتی دنبال می‌کنی.',
    'کاردکس سابقهٔ ثبت‌شده است؛ مشاهدهٔ آن جای شمارش فیزیکی انبار را نمی‌گیرد.',
    'inventory',
  ),
  entry(
    ['/inventory/receipts', '/inventory/movements'],
    'inventory-receipts',
    'رسید انبار',
    'ورود کالا به یک انبار و محل مشخص را ثبت یا رسیدهای قبلی را بررسی می‌کنی.',
    'پنج بسته کاغذ واقعاً به انبار مرکزی رسیده است؛ مقدار و محل ورود آن را در رسید مشخص می‌کنی.',
    [
      'ابتدا رسیدهای موجود را برای جلوگیری از ثبت تکراری بررسی کن.',
      'در فرم ورود، کالا، انبار، محل، مقدار و اطلاعات الزامی را کامل کن.',
      'پیش از ثبت، اطلاعات را با کالای واقعاً دریافت‌شده تطبیق بده.',
    ],
    'ورود کالا در سوابق انبار قابل پیگیری می‌شود.',
    'خرید یا ثبت سفارش به معنی رسیدن فیزیکی کالا نیست؛ رسید را مطابق دریافت واقعی ثبت کن.',
  ),
  entry(
    ['/inventory/issues'],
    'inventory-issues',
    'حوالهٔ خروج',
    'خروج کالا از انبار را ثبت می‌کنی و سوابق خروج‌های قبلی را می‌بینی.',
    'سه بسته کالا از انبار به تحویل‌گیرنده داده شده است؛ کالا، مقدار و محل خروج باید همان موارد واقعی باشند.',
    [
      'کالا، انبار و محل خروج را مشخص کن.',
      'مقدار قابل استفاده و مقدار خروج را کنترل کن.',
      'اطلاعات و مرجع عملیات را بررسی کن و پس از تأیید ثبت، جزئیات سند را ببین.',
    ],
    'خروج کالا و مرجع آن در سوابق انبار ثبت می‌شود.',
    'برای رفع خطای کمبود موجودی، رسید یا خروج صوری نساز؛ ابتدا رزروها و گردش کالا را بررسی کن.',
  ),
  entry(
    ['/inventory/transfers'],
    'inventory-transfers',
    'انتقال بین انبارها',
    'جابه‌جایی کالا از یک انبار به انبار دیگر را پیگیری می‌کنی.',
    'کالا از انبار مرکزی برای شعبه فرستاده شده، ولی هنوز به مقصد نرسیده است؛ انتقال باید همین وضعیت را نشان بدهد.',
    [
      'کالا، مقدار، مبدأ و مقصد را مشخص کن.',
      'مرحلهٔ انتقال را مطابق خروج و حمل واقعی جلو ببر.',
      'دریافت در مقصد را از جزئیات سند و فقط پس از وصول واقعی تأیید کن.',
    ],
    'محل و مرحلهٔ جابه‌جایی کالا میان مبدأ و مقصد روشن می‌شود.',
    'ارسال از مبدأ و وصول در مقصد دو مرحله‌اند؛ در راه بودن کالا را با موجود شدن آن در مقصد یکی ندان.',
  ),
  entry(
    ['/inventory/adjustments', '/inventory/counts'],
    'inventory-adjustments',
    'اصلاح موجودی',
    'مغایرت موجودی ثبت‌شده با شمارش واقعی را با دلیل مشخص اصلاح می‌کنی.',
    'پس از بررسی، موجودی واقعی یک کالا کمتر از ماندهٔ سیستم است؛ کسری باید با دلیل و مشخصات درست ثبت شود.',
    [
      'کالا، انبار و محل را انتخاب کن و موجودی فعلی را بررسی کن.',
      'موجودی شمارش‌شده، دلیل و اطلاعات ارزش‌گذاری لازم را وارد کن.',
      'اختلاف و پیام تأیید قطعی را با دقت بخوان و فقط پس از بررسی مسئول مربوط ثبت کن.',
    ],
    'اصلاح و علت آن در سابقهٔ انبار ثبت می‌شود.',
    'ثبت قطعی اصلاح برگشت ساده ندارد؛ از این صفحه برای پوشاندن رسید یا حوالهٔ جاافتاده استفاده نکن.',
  ),
  entry(
    ['/inventory/reservations'],
    'inventory-reservations',
    'رزرو کالا',
    'کالای کنارگذاشته‌شده برای یک نیاز یا سفارش را می‌بینی و رزروها را پیگیری می‌کنی.',
    'بخشی از کالا برای یک سفارش کنار گذاشته شده است؛ آن بخش نباید دوباره به سفارش دیگری قول داده شود.',
    [
      'برای رزرو تازه، کالا، انبار، محل، مقدار و مرجع را در فرم «رزرو جدید» مشخص کن.',
      'برای رزرو قبلی، مقدار و وضعیت آن را در فهرست بررسی کن.',
      '«آزادسازی» را برای برداشتن رزرو و «تحویل (مصرف)» را فقط برای تحویل واقعی و با مقدار درست به‌کار ببر.',
    ],
    'می‌فهمی چه بخشی از کالا در اختیار سفارش‌های دیگر است.',
    'رزرو به معنی خروج فیزیکی کالا نیست؛ موجودی کل و مقدار قابل استفاده ممکن است متفاوت باشند.',
  ),
  entry(
    ['/inventory/count-sessions'],
    'inventory-count-list',
    'انبارگردانی رسمی',
    'برای شمارش واقعی کالاهای یک انبار، جلسهٔ انبارگردانی ایجاد و جلسات قبلی را پیگیری می‌کنی.',
    'تیم انبار می‌خواهد موجودی پایان دوره را بشمارد؛ یک جلسه برای انبار و تاریخ مشخص ساخته می‌شود.',
    [
      'فهرست جلسات را بررسی کن تا برای یک شمارش، جلسهٔ تکراری نسازی.',
      '«سشن انبارگردانی جدید» را باز کن و انبار و تاریخ را مشخص کن.',
      'جلسه را باز کن و شمارش را در برگهٔ همان جلسه ادامه بده.',
    ],
    'مبنای شمارش و تصویر موجودیِ مرتبط با جلسه آماده می‌شود.',
    'ایجاد جلسه با ثبت نهایی کسری و اضافی فرق دارد؛ تأیید نهایی یک اقدام جدا و حساس است.',
  ),
  entry(
    ['/inventory/count-sessions/:sessionId'],
    'inventory-count-detail',
    'شمارش و مغایرت انبارگردانی',
    'مقدار واقعی هر قلم را در جلسهٔ انبارگردانی وارد و اختلاف آن با سیستم را بررسی می‌کنی.',
    'برای یک کالا سیستم ده عدد نشان می‌دهد، ولی شمارش واقعی نه عدد است؛ یک عدد کسری باید بررسی شود.',
    [
      'انبار و تاریخ جلسه را کنترل کن و مقدار واقعی هر ردیف را وارد کن.',
      '«ذخیرهٔ شمارش و محاسبهٔ مغایرت» را بزن و اختلاف‌ها و یادداشت‌ها را بررسی کن.',
      'فقط پس از تأیید مسئول مربوط، اقدام نهاییِ صدور اسناد کسری و اضافی را انجام بده.',
    ],
    'شمارش و مغایرت‌ها ثبت می‌شوند؛ در تأیید نهایی، اسناد تعدیل انبار صادر می‌شوند.',
    'ثبت نهایی طبق پیام صفحه قابل بازگشت نیست؛ پیش از آن، ردیف‌های شمارش‌نشده و علت مغایرت را بررسی کن.',
  ),
  entry(
    ['/inventory/reconciliation'],
    'inventory-reconciliation',
    'تطبیق انبار با سوابق و دفتر کل',
    'ناسازگاری میان گردش کالا، ماندهٔ موجودی، سریال‌ها و ارزش ثبت‌شده در حسابداری را بررسی می‌کنی.',
    'جمع گردش‌های یک کالا با ماندهٔ ثبت‌شده سازگار نیست؛ ابتدا گزارش پیش‌نمایش می‌گیری تا علت روشن شود.',
    [
      'محدودهٔ انبار، تاریخ و حساب مربوط را کنترل کن.',
      'ابتدا حالت پیش‌نمایش را برای بررسی بدون سند اصلاحی انتخاب کن.',
      'جزئیات مغایرت‌ها را بخوان؛ اجرای زنده و سند جبرانی را فقط پس از بررسی مجاز انجام بده.',
    ],
    'موارد ناسازگار مشخص می‌شوند تا علت هرکدام جداگانه بررسی شود.',
    'این تطبیق با شمارش فیزیکی کالا یکی نیست؛ اجرای زنده می‌تواند سند تعدیل غیرقابل‌ویرایش بسازد.',
  ),
  entry(
    ['/inventory/tracking-profiles'],
    'inventory-tracking',
    'شیوهٔ ردیابی کالا',
    'مشخص می‌کنی یک کالا با مقدار، بهر یا سریال و چه قواعدی ردیابی شود.',
    'برای دستگاه‌ها شمارهٔ سریال مهم است، اما برای یک محمولهٔ خوراکی ممکن است بهر و تاریخ انقضا مهم باشد.',
    [
      'کالای موردنظر را جست‌وجو کن و قرارداد فعلی و تاریخچه را ببین.',
      'در صورت مجوز، «ویرایش قرارداد» را باز کن و شیوهٔ ردیابی و الزام‌های تاریخ را بررسی کن.',
      'پیش از ذخیره، اثر تغییر بر گردش‌های قبلی و هر پیام محافظتی را بخوان.',
    ],
    'قاعدهٔ ثبت و پیگیری این کالا برای عملیات بعدی روشن می‌شود.',
    'فهرست برای سازمان فعلی است؛ بارگذاری یا خطا را «بدون قرارداد» فرض نکن و پس از بارگذاری دوباره، جست‌وجو و فیلترها را بررسی کن. تغییر ساختاری کالای دارای سابقه ممکن است مسدود باشد؛ مجوز مهاجرت راه میان‌بُر نیست.',
  ),
  entry(
    ['/inventory/lots'],
    'inventory-lots',
    'بهرها و محموله‌های کالا',
    'گروه‌های تولید یا دریافت یک کالا را جداگانه پیگیری می‌کنی؛ به هر گروه یک بهر گفته می‌شود.',
    'دو محموله از یک محصول، تاریخ انقضای متفاوت دارند؛ هر محموله باید با بهر خودش قابل تشخیص باشد.',
    [
      'کالا و بهر موردنظر را جست‌وجو کن؛ اگر بهر تازه‌ای لازم است، «ثبت بهر جدید» را باز کن.',
      'کد بهر، تاریخ‌ها و وضعیت اولیه را با محمولهٔ واقعی تطبیق بده.',
      'پیش از تغییر وضعیت یا ترخیص از قرنطینه، نتیجهٔ بررسی و پیام تأیید را کنترل کن.',
    ],
    'می‌توانی محموله‌های یک کالای یکسان را از هم تشخیص بدهی.',
    'فهرست بهرها برای سازمان فعلی است؛ بارگذاری یا خطا را «بدون بهر» فرض نکن و پس از بارگذاری دوباره، جست‌وجو و فیلترها را بررسی کن. بهر گروهی از کالاهاست و با سریال یک قلم فرق دارد.',
  ),
  entry(
    ['/inventory/serials'],
    'inventory-serials',
    'سریال‌های کالا',
    'هر قلم کالای سریال‌دار را با شناسهٔ اختصاصی خودش پیگیری می‌کنی.',
    'دو دستگاه از یک مدل داری، ولی هرکدام شمارهٔ سریال جدا دارند؛ باید مشخص باشد کدام دستگاه جابه‌جا شده است.',
    [
      'کالا یا شمارهٔ سریال موردنظر را پیدا کن و وضعیت و تاریخچهٔ آن را ببین.',
      'برای شناسه‌های تازه، «ورود گروهی سریال» را باز کن و کالا و شماره‌ها را مطابق روش انتخابی وارد کن.',
      'پیش از ثبت، شماره‌ها و در صورت نیاز بهر و انبار را با کالای واقعی تطبیق بده.',
    ],
    'هویت هر قلم سریال‌دار در سوابق انبار قابل تشخیص می‌ماند.',
    'فهرست سریال‌ها برای سازمان فعلی است؛ بارگذاری یا خطا را «بدون سریال» فرض نکن و پس از بارگذاری دوباره، جست‌وجو و فیلترها را بررسی کن. کد مدل کالا با سریال فرق دارد؛ یک کالا می‌تواند چندین سریال داشته باشد.',
  ),
  entry(
    ['/inventory/uom-conversions'],
    'inventory-uom',
    'تبدیل واحدها',
    'رابطهٔ واحدها را تعریف می‌کنی؛ مثلاً هر کارتن چند عدد از یک کالا دارد.',
    'هر کارتن از یک کالا دوازده عدد است؛ ضریب تبدیل از کارتن به عدد، دوازده بر یک می‌شود.',
    [
      'واحد مبدأ و مقصد و دامنهٔ کالا را مشخص کن.',
      'صورت و مخرج ضریب را متناسب با رابطهٔ واقعی ثبت کن.',
      'با «آزمونگر تبدیل به واحد پایه» نتیجه را برای یک مقدار نمونه بررسی کن.',
    ],
    'مقدار ورودی با ضریب مشخص به واحد موردنیاز تبدیل می‌شود.',
    'ضریب سراسری برای همهٔ کالاهاست؛ اگر تعداد داخل کارتن بین کالاها فرق دارد، دامنهٔ کالا را مشخص کن.',
  ),
  entry(
    ['/inventory/reports'],
    'inventory-reports',
    'گزارش‌های انبار',
    'موجودی انبارها، هشدارهای سفارش مجدد و کالاهای راکد را بررسی می‌کنی.',
    'برای برنامهٔ خرید، کالاهای زیر حداقل را می‌بینی و گردش همان کالا را هم کنترل می‌کنی.',
    [
      'تب موجودی، نقطهٔ سفارش یا کالاهای راکد را انتخاب کن.',
      'بازه و محدودهٔ گزارش و هر توضیح دربارهٔ محدود بودن داده را بخوان.',
      'برای بررسی علت یک عدد، کاردکس کالای مربوط را باز کن.',
    ],
    'اقلام نیازمند خرید یا بررسی گردش مشخص می‌شوند.',
    'پیشنهاد سفارش، سفارش خرید ثبت‌شده نیست؛ اعداد را با محدودهٔ داده و واحد کالا بخوان.',
    'inventory',
  ),
  entry(
    ['/inventory/movements/:movementId'],
    'inventory-movement-detail',
    'جزئیات سند انبار',
    'مشخصات یک رسید، خروج، انتقال یا تعدیل و اقلام و ارتباط مالی آن را بررسی می‌کنی.',
    'می‌خواهی بدانی یک خروج از کدام انبار و برای چه مرجعی ثبت شده است؛ جزئیات همان سند را باز می‌کنی.',
    [
      'نوع، شماره، تاریخ و وضعیت سند را کنترل کن.',
      'کالا، مقدار، انبار، محل و اطلاعات بهر یا سریال را بررسی کن.',
      'اگر اقدام مالی یا تأیید وصول در دسترس است، تنها پس از بررسی مرجع و توضیح صفحه آن را انجام بده.',
    ],
    'مبنای گردش کالا و مدارک مرتبط با همین سند را می‌بینی.',
    'قبل از «ثبت سند حسابداری»، سند مرتبط موجود را بررسی کن تا برای یک حرکت، ثبت دستی تکراری انجام ندهی.',
  ),
  entry(
    ['/procurement'],
    'procurement-home',
    'تدارکات و خرید',
    'نیازهای خرید، سفارش‌های تأمین‌کنندگان و مرحلهٔ تأمین کالا را مرور می‌کنی.',
    'انبار به کالا نیاز دارد؛ درخواست خرید بررسی می‌شود و بعد سفارش خرید برای تأمین‌کننده شکل می‌گیرد.',
    [
      'خلاصهٔ خرید و موارد منتظر اقدام را مرور کن.',
      'بر اساس مرحله، تب درخواست‌ها، سفارش‌ها یا تأمین‌کنندگان را باز کن.',
      'جزئیات مورد مربوط را بررسی کن تا مرحلهٔ بعد را درست انجام بدهی.',
    ],
    'می‌فهمی نیاز خرید در چه مرحله‌ای قرار دارد.',
    'درخواست خرید، سفارش خرید و رسید انبار یک سند واحد نیستند؛ هرکدام مرحلهٔ متفاوتی را ثبت می‌کنند.',
  ),
  entry(
    ['/procurement/requests'],
    'procurement-requests',
    'درخواست‌های خرید',
    'نیاز شرکت به کالا یا خدمت را برای بررسی و تصمیم‌گیری ثبت می‌کنی.',
    'واحد اداری به چند بسته کاغذ برای هفتهٔ آینده نیاز دارد؛ مقدار و زمان نیاز را درخواست می‌کند.',
    [
      'فهرست درخواست‌ها را برای نیاز مشابه یا درخواست قبلی بررسی کن.',
      'در فرم درخواست، اقلام، مقدار و اطلاعات الزامی را مشخص کن.',
      'درخواست ثبت‌شده را باز کن و مرحلهٔ ارسال و بررسی آن را دنبال کن.',
    ],
    'نیاز خرید به‌شکل قابل پیگیری به مسئول مربوط اعلام می‌شود.',
    'ایجاد درخواست به معنی تأیید بودجه، خرید از فروشنده یا ورود کالا به انبار نیست.',
  ),
  entry(
    ['/procurement/requests/:ref'],
    'procurement-request-detail',
    'بررسی درخواست خرید',
    'اقلام و علت یک درخواست خرید و سابقهٔ تأیید یا رد آن را می‌بینی.',
    'مدیر باید درخواست کاغذ را بررسی کند؛ مقدار، زمان نیاز و هدف خرید را می‌خواند.',
    [
      'اقلام، موعد نیاز، انبار مقصد و هدف درخواست را کنترل کن.',
      'اقدام مجازِ متناسب با وضعیت، مثل ارسال یا تأیید درخواست را انتخاب کن.',
      'اگر درخواست تأیید شده و مجوز داری، «ساخت سفارش خرید» را برای مرحلهٔ بعد باز کن.',
    ],
    'تصمیم دربارهٔ درخواست و ارتباط آن با سفارش خرید قابل پیگیری می‌شود.',
    'برای رد درخواست دلیل ثبت کن؛ تأیید درخواست به معنی دریافت کالا نیست.',
  ),
  entry(
    ['/procurement/orders'],
    'procurement-orders',
    'سفارش‌های خرید',
    'خرید مورد توافق با تأمین‌کننده را ثبت و مرحلهٔ تحویل آن را دنبال می‌کنی.',
    'پس از تأیید نیاز، بیست بسته از یک تأمین‌کننده سفارش می‌دهی و منتظر ارسال آن می‌مانی.',
    [
      'سفارش موجود را با شماره یا تأمین‌کننده پیدا کن.',
      'برای مورد تازه، تأمین‌کننده، اقلام و اطلاعات الزامی را مشخص کن.',
      'جزئیات سفارش را باز کن تا ارسال، تأیید و دریافت کالا را پیگیری کنی.',
    ],
    'تعهد خرید و مرحلهٔ تأمین آن در یک سفارش قابل پیگیری است.',
    'ثبت سفارش خرید به‌تنهایی موجودی انبار را زیاد نمی‌کند؛ دریافت واقعی مرحلهٔ جداگانه دارد.',
  ),
  entry(
    ['/procurement/orders/:ref'],
    'procurement-order-detail',
    'جزئیات سفارش خرید',
    'اقلام سفارش خرید، مرحلهٔ تأیید و حمل و دریافت واقعی آن را پیگیری می‌کنی.',
    'از سفارش بیست‌بسته‌ای، فعلاً دوازده بسته رسیده است؛ مقدار واقعی دریافت را برای همان مرحله بررسی می‌کنی.',
    [
      'تأمین‌کننده، اقلام، مقدارها و وضعیت سفارش را کنترل کن.',
      'ارسال برای تأیید یا ثبت ارسال بار را فقط مطابق مرحلهٔ واقعی انجام بده.',
      'در زمان دریافت، «ثبت رسید انبار» را باز کن و انبار، محل و مقدار واقعاً رسیده را بررسی کن.',
    ],
    'مشخص می‌شود چه مقدار سفارش داده شده، چه مقدار رسیده و چه مقدار باقی مانده است.',
    'برای کامل‌شدن ظاهری سفارش، مقدار دریافت را بیشتر از کالای رسیده وارد نکن؛ هزینهٔ جانبی هم اقدام جداگانه دارد.',
  ),
  entry(
    ['/procurement/suppliers'],
    'procurement-suppliers',
    'تأمین‌کنندگان',
    'طرف‌حساب‌هایی را پیدا می‌کنی که نقش تأمین‌کننده دارند.',
    'پیش از سفارش خرید از شرکت آفتاب، می‌خواهی نام و مشخصات تأمین‌کنندهٔ درست را بررسی کنی.',
    [
      'نام یا کد تأمین‌کننده را جست‌وجو کن و نتیجه‌های موجود را بررسی کن.',
      'برای طرف‌حسابی که از قبل وجود دارد، نقش تأمین‌کننده را در اطلاعات پایه بررسی کن.',
      'برای شخص یا شرکت واقعاً تازه، «ثبت سریع تأمین‌کننده» را باز کن و مشخصاتش را کنترل و ثبت کن.',
    ],
    'تأمین‌کنندهٔ درست برای ادامهٔ فرایند خرید مشخص می‌شود.',
    'یک شرکت می‌تواند هم مشتری و هم تأمین‌کننده باشد؛ برای نقش دوم رکورد تکراری نساز.',
    'parties',
  ),
  entry(
    ['/projects'],
    'projects-home',
    'پروژه‌ها و کارها',
    'وضعیت پروژه‌ها، کارها و موارد عقب‌افتاده را مرور می‌کنی.',
    'برای راه‌اندازی دفتر جدید چند کار به افراد مختلف سپرده شده است؛ پیشرفت و موعد آن‌ها را از این بخش دنبال می‌کنی.',
    [
      'خلاصهٔ پروژه‌ها و کارهای باز را مرور کن.',
      'برای موضوع موردنظر به فهرست پروژه‌ها یا کارها برو.',
      'جزئیات مورد مربوط را باز کن و مسئول و موعد آن را بررسی کن.',
    ],
    'کارهای باقی‌مانده و مسئول پیگیری هرکدام روشن‌تر می‌شود.',
    'پروژه یک مجموعه کار است؛ کار شخصی و یادآور را با وظیفهٔ رسمی پروژه یکی ندان.',
  ),
  entry(
    ['/projects/list'],
    'projects-list',
    'فهرست پروژه‌ها',
    'پروژه‌های در دسترس را پیدا می‌کنی و وضعیت و موعد هرکدام را می‌بینی.',
    'برای بررسی راه‌اندازی دفتر، پروژهٔ مربوط را پیدا می‌کنی و وارد جزئیاتش می‌شوی.',
    [
      'پروژه را در فهرست پیدا کن و وضعیت آن را بررسی کن.',
      'برای مورد تازه، اطلاعات الزامی فرم ایجاد پروژه را مشخص کن.',
      'پروژه را باز کن تا تیم، کارها و موعدها را ببینی.',
    ],
    'به فضای کاری مربوط به همان پروژه می‌رسی.',
    'پروژه‌های هم‌نام ممکن است موضوع متفاوتی داشته باشند؛ کد، مسئول و موعد را هم کنترل کن.',
  ),
  entry(
    ['/projects/list/:ref'],
    'project-detail',
    'جزئیات پروژه',
    'تیم، کارها، وضعیت و گفت‌وگوهای مربوط به یک پروژه را کنار هم می‌بینی.',
    'در پروژهٔ راه‌اندازی دفتر، کار نصب تجهیزات را به یکی از اعضای فعال تیم می‌سپاری.',
    [
      'نام و وضعیت پروژه و اعضای تیم را کنترل کن.',
      'کارهای موجود را مرور کن؛ در صورت مجوز از «کار جدید» برای وظیفهٔ تازه استفاده کن.',
      'وضعیت پروژه را فقط مطابق نتیجهٔ واقعی و با توجه به پیام تأیید تغییر بده.',
    ],
    'مسئولیت‌ها و پیشرفت همین پروژه قابل پیگیری می‌شود.',
    'لغو پروژه با توقف موقت فرق دارد؛ پیام صفحه می‌گوید پروژهٔ لغوشده دوباره فعال نمی‌شود.',
  ),
  entry(
    ['/projects/tasks'],
    'projects-tasks',
    'کارهای پروژه',
    'وظایف پروژه‌ها را با مسئول، سررسید، اولویت و وضعیت می‌بینی.',
    'می‌خواهی بدانی نصب تجهیزات به چه کسی سپرده شده و تا چه زمانی باید تمام شود.',
    [
      'کار موردنظر را در فهرست پیدا کن.',
      'پروژه، مسئول، موعد و وضعیت آن را بررسی کن.',
      'جزئیات کار را باز کن تا مراحل اجرایی و نتیجه را دنبال کنی.',
    ],
    'می‌دانی چه کاری، توسط چه کسی و در چه زمانی باید انجام شود.',
    'دیدن عنوان مشابه کافی نیست؛ پروژه و کد کار را هم بررسی کن تا روی وظیفهٔ اشتباه اقدام نکنی.',
  ),
  entry(
    ['/projects/tasks/:ref'],
    'project-task-detail',
    'جزئیات و اجرای کار',
    'وضعیت یک کار، مراحل چک‌لیست و زمان صرف‌شده برای آن را بررسی یا ثبت می‌کنی.',
    'وظیفهٔ نصب تجهیزات شروع شده است؛ مراحل انجام را علامت می‌زنی و زمان واقعی کار را ثبت می‌کنی.',
    [
      'عنوان، پروژه، مسئول و موعد را کنترل کن.',
      'مراحل انجام‌شدهٔ چک‌لیست و در صورت نیاز کارکرد زمانی را ثبت کن.',
      'از اقدام‌های وضعیت، مثل «شروع کار» یا «انجام شد»، مطابق نتیجهٔ واقعی استفاده کن.',
    ],
    'وضعیت کار و سابقهٔ اجرای آن برای اعضای مجاز روشن می‌شود.',
    'ثبت زمان به معنی انجام شدن کار نیست؛ لغو کار هم با تکمیل کار فرق دارد.',
  ),
  entry(
    ['/projects/todos'],
    'projects-todos',
    'کارهای شخصی',
    'کارهای کوتاه شخصی را سریع یادداشت می‌کنی و پس از انجام، آن‌ها را می‌بندی.',
    'یادت می‌افتد باید برای جلسه یک فایل آماده کنی؛ عنوان آن را در فهرست شخصی می‌نویسی.',
    [
      'عنوان کار را در ورودی یادداشت جدید بنویس.',
      'با «افزودن» یا Enter آن را ثبت کن.',
      'پس از انجام، همان مورد را انجام‌شده علامت بزن؛ اگر منتفی شده، وضعیت مناسب را انتخاب کن.',
    ],
    'یادآوری کوتاه از ذهنت به فهرست کارهای شخصی منتقل می‌شود.',
    'برای کاری که مسئول، پروژه و پیگیری تیمی لازم دارد، از وظیفهٔ پروژه استفاده کن.',
  ),
  entry(
    ['/projects/reminders'],
    'projects-reminders',
    'یادآورها',
    'موضوعی را برای یادآوری در زمان مشخص ثبت و وضعیت یادآورهای قبلی را بررسی می‌کنی.',
    'می‌خواهی فردا برای هماهنگی جلسه دوباره موضوعی را بررسی کنی؛ زمان یادآور را مشخص می‌کنی.',
    [
      'فهرست و زمان یادآورها را بررسی کن.',
      'برای مورد تازه، عنوان و زمان موردنیاز و فیلدهای الزامی را وارد کن.',
      'پس از ذخیره، زمان و وضعیت همان یادآور را کنترل کن.',
    ],
    'موضوع همراه با زمان موردنیاز برای پیگیری نگه‌داری می‌شود.',
    'ثبت یادآور تضمین دریافت اعلان روی هر دستگاه نیست؛ تنظیمات اعلان و دسترسی دستگاه را جدا بررسی کن.',
  ),
  entry(
    ['/projects/overdue'],
    'projects-overdue',
    'کارهای عقب‌افتاده',
    'کارهایی را می‌بینی که موعدشان گذشته و هنوز نیازمند پیگیری‌اند.',
    'موعد نصب تجهیزات گذشته ولی کار هنوز باز است؛ باید با مسئول آن دربارهٔ وضعیت واقعی صحبت کنی.',
    [
      'موارد عقب‌افتاده را مرور کن.',
      'مسئول، پروژه و سررسید هر مورد را بررسی کن.',
      'جزئیات کار را باز کن و نتیجهٔ پیگیری را در همان کار ثبت کن.',
    ],
    'می‌توانی پیگیری را از کارهایی شروع کنی که موعدشان گذشته است.',
    'گذشتن موعد به معنی انجام یا لغو کار نیست؛ وضعیت را پس از بررسی نتیجهٔ واقعی تغییر بده.',
  ),
  entry(
    ['/projects/comments'],
    'projects-comments',
    'نظرات پروژه و کار',
    'گفت‌وگو و توضیح مربوط به یک پروژه یا کار مشخص را می‌خوانی و ثبت می‌کنی.',
    'برای نصب تجهیزات یک نکتهٔ مهم وجود دارد؛ نظر را زیر همان کار ثبت می‌کنی تا در سابقه‌اش بماند.',
    [
      'ابتدا نوع «پروژه» یا «کار» و سپس مورد مربوط را انتخاب کن.',
      'نظرهای قبلی را بخوان تا موضوع تکرار نشود.',
      'متن روشن و مرتبط بنویس و با «ثبت نظر» ارسال کن.',
    ],
    'توضیح در سابقهٔ همان پروژه یا کار باقی می‌ماند.',
    'نوشتن نظر به‌تنهایی مسئول، موعد یا وضعیت کار را تغییر نمی‌دهد.',
  ),
  ...(
    [
      ['crm', 'فروش'],
      ['inventory', 'انبار'],
      ['procurement', 'خرید'],
      ['projects', 'پروژه‌ها'],
    ] as const
  ).map(([module, label]) =>
    entry(
      [`/${module}/assistant`],
      `${module}-assistant`,
      `دستیار ${label}`,
      `از دستیار همین بخش برای بررسی سؤال‌های مرتبط با ${label} استفاده می‌کنی؛ فعال بودن و دسترسی آن در صفحه مشخص است.`,
      `می‌خواهی یک موضوع مربوط به ${label} را بررسی کنی؛ از پیشنهادهای موجود شروع می‌کنی و محدودهٔ سؤال را روشن می‌نویسی.`,
      [
        'وضعیت فعال یا غیرفعال بودن دستیار را بررسی کن.',
        'یک پیشنهاد موجود را انتخاب کن یا سؤال مشخص و مرتبط با همین بخش بنویس.',
        'پاسخ و هر پیام تأیید یا مصرف اعتبار را بخوان و نتیجه را با داده‌های قابل مشاهده بررسی کن.',
      ],
      'در محدودهٔ فعال و مجازِ دستیار، پاسخ مرتبط با سؤال دریافت می‌کنی.',
      'پاسخ تحلیلی به‌خودی‌خود تأیید یک عملیات یا سند قطعی نیست؛ ثبت هر عملیات را در صفحهٔ مربوط بررسی کن.',
    ),
  ),
];
